import React, { useState } from 'react';
import { RiArrowDownSLine } from '@remixicon/react';
import TrustScoreStatusPill from './TrustScoreStatusPill';
import { STATUS_STYLES } from '../utils/thresholds';

/**
 * A single TrustScore factor row — collapsed by default, expands to
 * reveal the API's technical description (parsed from the part after
 * " - " in the raw description string).
 *
 * Gradient background + gradient border are painted via the modern
 * `background-clip: padding-box, border-box` trick (see STATUS_STYLES
 * in thresholds.js). This produces a real translucent card fill with
 * an opaque gradient border, respects border-radius, and — unlike the
 * padded-wrapper approach — doesn't bleed the border color through the
 * translucent card, so the 40% alpha in Figma actually reads correctly.
 */
const TrustScoreFactorCard = ({ title, body, status, defaultOpen = false }) => {
  const [open, setOpen] = useState(!!defaultOpen);
  const style = STATUS_STYLES[status] || STATUS_STYLES.acceptable;

  const toggle = () => setOpen((prev) => !prev);
  // Click on the row acts as the toggle. Keyboard support: Space and
  // Enter both fire it so the whole card is a real button semantically.
  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    }
  };

  return (
    // Outer card: positioned relative so the absolute ring below can
    // sit on top of it. Only the translucent bg is applied here — the
    // gradient border rides on its own layer, so the fill's alpha is
    // preserved (setting CARD_FILL_ALPHA = 0 truly makes the card body
    // transparent; the ring stays visible so the layout doesn't break).
    <div
      className="relative rounded-[16px] overflow-hidden transition-all"
      style={style.cardStyle.body}
    >
      {/* Gradient border ring — mask cuts out the interior of the box,
          leaving only a 1px perimeter of the gradient visible. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 rounded-[16px]"
        style={style.cardStyle.borderRing}
      />
      <button
        type="button"
        onClick={toggle}
        onKeyDown={onKeyDown}
        aria-expanded={open}
        className="relative w-full flex items-center justify-between gap-3 px-5 py-4 text-left"
      >
        <h3 className="text-sm md:text-base font-medium text-gray-900 flex-1 min-w-0 truncate">
          {title}
        </h3>
        <div className="flex items-center gap-2 flex-shrink-0">
          <TrustScoreStatusPill status={status} />
          {/* Chevron color pinned to the Figma hex (#5C5C5C) — deliberately
              darker than the Tailwind text-gray-400 default so it reads
              clearly against the pale gradient backgrounds. */}
          <RiArrowDownSLine
            className={`w-5 h-5 text-[#5C5C5C] transition-transform ${open ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
        </div>
      </button>
      {open && body && (
        <div className="relative px-5 pb-4 -mt-1">
          <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{body}</p>
        </div>
      )}
    </div>
  );
};

export default TrustScoreFactorCard;
